import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import { BehaviorSubject, merge, Observable } from 'rxjs'
import { finalize, map, tap, withLatestFrom } from 'rxjs/operators'
import * as lodash from 'lodash'

import * as fromRoot from '@tv3/store/state'
import {
  Currency,
  DateRangePickerSelection,
  createLocaleCompareSort,
  isEmptyTable,
  cleanRateMappings,
  ActionFailed,
  LoadGroupRatesComplete,
  RatesChangedComplete,
  loadRentalRates,
} from '@tokeet-frontend/tv3-platform'
import { TableType } from '@tv3/shared/empty-table/table-type'

import {
  ConfirmDialogService,
  GroupRateService,
  GroupRatesChangedComplete,
  Rate,
  RateService,
  RateType,
  Rental,
  RentalService,
  selectAllRentals,
  Toaster,
  toMoment,
  untilDestroy,
  updateRentalComplete,
} from '@tokeet-frontend/tv3-platform'
import { SelectableRow } from '@tokeet-frontend/tv3-platform'
import { UserStorage, selectRentalsWithoutSubUnits } from '@tokeet-frontend/tv3-platform'
import { EditRateDialogService } from '../edit-rate-dialog/edit-rate-dialog.service'
import { EditRateDialogInput } from '../edit-rate-dialog/edit-rate-dialog.component'
import { AccountRatesDialogService } from '../account-rates-dialog/account-rates-dialog.service'
import { AccountRatesDialogTab } from '../account-rates-dialog/account-rates-dialog.component'
import * as R from 'ramda'

@Component({
  selector: 'app-rates-table',
  templateUrl: './rates-table.component.html',
  styleUrls: ['./rates-table.component.scss'],
})
export class RatesTableComponent extends SelectableRow<Rate> implements OnInit, OnChanges {
  @Input() rental: Rental

  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  isDeleting = false
  groupRatesLoaded = false

  tableType: TableType

  searchControl = new FormControl()
  categoryControl = new FormControl()
  periodControl = new FormControl()
  rentalsControl = new FormControl()

  rentalRates$ = new BehaviorSubject<Rate[]>([])
  groupRates$ = new BehaviorSubject<Rate[]>([])

  rates$ = new BehaviorSubject<Rate[]>([])

  filteredRentals$ = new BehaviorSubject<Rental[]>([])
  categories$: Observable<string[]>

  rentals: Rental[]

  currency: string | Currency

  displayedColumns = [
    'select',
    'name',
    'category',
    'period',
    'nightly',
    'weekly',
    'monthly',
    'minimumStay',
    'touch',
    'edit',
  ]

  isEmptyTable$ = isEmptyTable(this.dataSource)

  isSelectAllRenderedItems = true

  constructor(
    protected storage: UserStorage,
    private store: Store<fromRoot.State>,
    private actions$: Actions,
    private toaster: Toaster,
    private rentalService: RentalService,
    private rateService: RateService,
    private groupRateService: GroupRateService,
    private confirmDialog: ConfirmDialogService,
    private editRateDialog: EditRateDialogService,
    private accountRatesDialog: AccountRatesDialogService
  ) {
    super(storage)
  }

  ngOnInit() {
    if (!this.isGroupRates && this.rental) {
      this.store.dispatch(loadRentalRates({ pkeys: [this.rental.id] }))
    }
    this.tableType = this.isGroupRates ? TableType.GroupRates : TableType.RentalRates
    this.currency = this.isGroupRates ? null : this.rental.currency

    this.loadSavedTableState()

    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = createLocaleCompareSort(this.sortDataAccessor)

    this.categories$ = this.rates$.pipe(
      map((rates) => this.rentalService.getUniqueCategories(rates)),
      tap((items) => {
        const chosenItem = this.categoryControl.value

        if (R.isNil(R.find((i) => i === chosenItem, items))) {
          this.categoryControl.patchValue(null)
        }
      })
    )

    if (this.isGroupRates) {
      this.prepareRentals()
      this.displayedColumns.splice(4, 0, 'rentals')
    }

    this.prepareRates()

    merge(
      this.rates$,
      this.searchControl.valueChanges,
      this.categoryControl.valueChanges,
      this.periodControl.valueChanges,
      this.rentalsControl.valueChanges
    )
      .pipe(
        tap(() => {
          this.filterRates()
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.rates$.next(this.rates$.value)
  }

  ngOnChanges(changes: SimpleChanges) {
    this.rental = lodash.get(changes, ['rental', 'currentValue'], this.rental)

    if (!this.isGroupRates) this.prepareRentalRates()
  }

  isRowSelectable = (rate: Rate) => {
    return this.isGroupRates || !this.isGroupRate(rate)
  }

  onMasterToggle(checked: boolean) {
    if (checked) {
      this.isSelectAllRenderedItems = true
      this.masterToggle(true)
    } else {
      this.unselectAll()
    }
  }

  onSelectAll(all = false) {
    this.unselectAll()
    if (all) {
      this.masterToggle(false)
      this.isSelectAllRenderedItems = false
    } else {
      this.masterToggle(true)
      this.isSelectAllRenderedItems = true
    }
  }

  searchRate(rate: Rate) {
    this.searchControl.setValue(rate.name)
  }

  hasRentalRates = (rates: Rate[]) => {
    return !!rates.find((rate) => !this.isGroupRate(rate))
  }

  get isGroupRates() {
    return !this.rental
  }

  get rateLabel() {
    return this.isGroupRates ? $localize`Group Rate` : $localize`Rate`
  }

  get rateIcon() {
    return this.isGroupRates ? 'fas fa-hands-usd' : 'far fa-calculator'
  }

  isGroupRate(rate: Rate) {
    return !rate.rentalId && rate.rentalIds
  }

  sortDataAccessor = (rate: Rate, property: string) => {
    switch (property) {
      case 'period':
        return rate.start
      case 'category':
        return this.getRateCategory(rate)
      case 'rentals':
        return rate.rentalIds ? rate.rentalIds.length : 0
      default:
        return rate[property]
    }
  }

  clearFilters() {
    this.searchControl.reset()
    this.categoryControl.reset()
    this.periodControl.reset()
    this.rentalsControl.reset()
  }

  get isFiltering() {
    return (
      this.searchControl.value || this.categoryControl.value || this.periodControl.value || this.rentalsControl.value
    )
  }

  filterRates() {
    const search = lodash.trim(this.searchControl.value).toLowerCase()
    const category = lodash.toString(this.categoryControl.value)
    const period = this.periodControl.value as DateRangePickerSelection

    let rates = this.rates$.value

    if (search) {
      const dateFormat = 'MMM D, YYYY'

      rates = rates.filter((rate) => {
        if (lodash.toString(rate.name).toLowerCase().includes(search)) return true
        if (lodash.toString(rate.category).toLowerCase().includes(search)) return true

        const textCategories = rate.categories && lodash.toString(rate.categories.join('\n'))
        if (textCategories && textCategories.toLowerCase().includes(search)) return true

        const textPeriod = `${toMoment(rate.start).format(dateFormat)} - ${toMoment(rate.end).format(dateFormat)}`
        if (textPeriod && textPeriod.toLowerCase().includes(search)) return true

        return false
      })
    }

    if (category && category !== '*') {
      rates = rates.filter((rate) => {
        return lodash.toString(rate.category) === category || (rate.categories && rate.categories.includes(category))
      })
    }

    if (period) {
      rates = rates.filter((rate) => {
        const start = toMoment(rate.start).startOf('date').unix()
        const end = toMoment(rate.end).startOf('date').unix()

        return (
          (start >= period.from && start <= period.to) ||
          (end >= period.from && end <= period.to) ||
          (start < period.from && end > period.to)
        )
      })
    }

    if (this.isGroupRates) {
      const rentalIds = lodash.keyBy(this.rentalsControl.value as string[])

      if (!lodash.isEmpty(rentalIds)) {
        rates = rates.filter((rate) => {
          return lodash.find(rate.rentalIds, (rentalId) => rentalIds[rentalId])
        })
      }
    }

    this.dataSource.data = rates
  }

  getRateCategory(rate: Rate) {
    if (rate.type === 'promotion') {
      return (rate.categories || []).join(', ') || 'promotion'
    } else {
      return rate.category || ''
    }
  }

  getRowTooltip = (rate: Rate) => {
    if (this.isGroupRates || !this.isGroupRate(rate)) return null
    return $localize`This is a group rate and may only be managed in the group rates table.`
  }

  getRentalsTooltip = (rentalIds: string) => {
    const rentals = lodash.keyBy(this.rentals, (rental) => rental.id)

    const rentalNames = lodash.map(rentalIds, (rentalId) => {
      const rental = rentals[rentalId]

      return rental && rental.name
    })

    return lodash.compact(rentalNames).join(', ')
  }

  onAddStandardRate() {
    this.openEditRateDialog(null, 'standard')
  }

  onAddDynamicRate() {
    this.openEditRateDialog(null, 'dynamic')
  }

  onEdit(rate: Rate) {
    if (!this.isGroupRates && this.isGroupRate(rate)) {
      this.accountRatesDialog.open({ activeTab: AccountRatesDialogTab.GroupRates, focusGroupRate: rate })
      return
    }

    this.openEditRateDialog(rate)
  }

  onDelete(rate?: Rate) {
    const rates: Rate[] = rate ? [rate] : this.getSelected()

    if (this.isGroupRates) {
      this.onDeleteGroupRates(rates)
    } else {
      this.onDeleteRentalRates(rates)
    }
  }

  private prepareRates() {
    this.prepareGroupRates()

    if (this.isGroupRates) {
      this.groupRates$
        .pipe(
          tap((rates) => {
            this.rates$.next(rates)
          }),
          untilDestroy(this)
        )
        .subscribe()

      return
    }

    merge(this.rentalRates$, this.groupRates$)
      .pipe(
        tap(() => {
          const rentalRates = [...this.rentalRates$.value]
          const groupRates = this.groupRates$.value

          groupRates.forEach((rate) => {
            if (lodash.includes(rate.rentalIds, this.rental.id)) {
              rentalRates.push(rate)
            }
          })

          this.rates$.next(rentalRates)
        })
      )
      .subscribe()
  }

  private prepareGroupRates() {
    this.groupRateService
      .all()
      .pipe(
        tap((rates) => {
          this.groupRates$.next(rates)
          this.groupRatesLoaded = true
        })
      )
      .subscribe()

    this.actions$
      .pipe(
        ofType(GroupRatesChangedComplete, LoadGroupRatesComplete),
        tap(({ rates }) => {
          this.groupRates$.next(rates)
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private prepareRentalRates() {
    const rates = [...(this.rental.rates || []), ...(this.rental.promotions || [])]

    this.rentalRates$.next(rates)
  }

  private prepareRentals() {
    this.rates$
      .pipe(
        withLatestFrom(this.store.select(selectRentalsWithoutSubUnits)),
        map(([rates, rentals]) => {
          this.rentals = rentals

          const rentalIds = lodash
            .chain(rates)
            .map((rate) => rate.rentalIds)
            .flatten()
            .keyBy()
            .value()

          rentals = rentals.filter((rental) => rentalIds[rental.id])

          this.filteredRentals$.next(rentals)
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private onDeleteGroupRates(rates: Rate[]) {
    const textRates = rates.length > 1 ? $localize`these group rates` : $localize`this group rate`

    const text = $localize`Are you sure you want to delete ` + textRates + `?`

    this.confirmDialog
      .confirm({
        title: $localize`Delete ` + textRates + `?`,
        body: $localize`This action may also delete rate mappings in rentals. ` + text,
      })
      .subscribe(() => {
        this.deleteGroupRates(rates)
      })
  }

  private onDeleteRentalRates(rates: Rate[]) {
    const textRates = rates.length > 1 ? $localize`these rates` : $localize`this rate`

    const text = $localize`Are you sure you want to delete ` + textRates + `?`

    this.confirmDialog
      .confirm({
        title: $localize`Delete ` + textRates + `?`,
        body: $localize`This action may also delete rate mappings. ` + text,
      })
      .subscribe(() => {
        this.deleteRentalRates(rates)
      })
  }

  private deleteGroupRates(rates: Rate[]) {
    this.isDeleting = true

    this.groupRateService
      .deleteRates(rates)
      .pipe(
        tap((updatedRates) => this.store.dispatch(GroupRatesChangedComplete({ rates: updatedRates }))),
        finalize(() => (this.isDeleting = false))
      )
      .subscribe(
        () => {
          this.toaster.success(
            (rates.length > 1 ? $localize`Group rates` : $localize`Group rate`) + $localize` deleted successfully.`
          )

          this.store.dispatch(
            cleanRateMappings({ rentalIds: lodash.uniq(lodash.flatten(lodash.map(rates, (r) => r.rentalIds || []))) })
          )
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  private deleteRentalRates(rates: Rate[]) {
    this.isDeleting = true

    this.rateService
      .deleteRates(this.rental.id, rates)
      .pipe(
        tap((d) =>
          this.store.dispatch(
            updateRentalComplete({
              update: { id: this.rental.id, changes: { rates: d.rates, promotions: d.promotions } },
            })
          )
        ),
        finalize(() => (this.isDeleting = false))
      )
      .subscribe(
        () => {
          this.toaster.success(
            (rates.length > 1 ? $localize`Rates` : $localize`Rate`) + $localize` deleted successfully.`
          )
          this.store.dispatch(
            cleanRateMappings({ rentalIds: lodash.uniq(lodash.flatten(lodash.map(rates, (r) => r.rentalIds || []))) })
          )
          this.store.dispatch(RatesChangedComplete({ rentalId: this.rental.id }))
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  private openEditRateDialog(rate: Rate = null, type: RateType = null) {
    let isGroupRate = false

    if ((!rate && this.isGroupRates) || (rate && this.isGroupRate(rate))) {
      isGroupRate = true
    }

    const params: EditRateDialogInput = {
      isGroupRate,
      rate,
      type,
    }

    if (isGroupRate) {
      params.groupRates = this.groupRates$.value
    } else {
      params.rental = this.rental
    }

    this.editRateDialog
      .open(params)
      .afterClosed()
      .subscribe((result) => {
        if (result && result.groupRates) {
          this.groupRates$.next(result.groupRates)
        }
      })
  }
}
